import { metricKeys } from '@pulse/metrics';
import { getMetricDisplayName } from '@utils/metrics';
import { CURRENT_PRODUCT_MODE, isEiModule } from '@utils/constants';
import {
  getUserDefinedDistributionMetric,
  getPreDefinedDistributionMetrics,
} from '@utils/metrics/metricHelpers';

const AXIS_NAME = {
  WAIT_TIME: 'Wait Time',
  CIR_TIME_PRECEDING_EXIT: 'CIR Time preceding Exit',
  AD_PLAYING_TIME: 'Ad Playing Time',
  PLAYING_TIME: 'Playing Time',
  PLAYS: 'Plays',
  LifeDropFrameRatio: 'Dropped Frames Ratio',
};

const ENGAGEMENT = {
  LifeRebufferingRatio: 'LifeRebufferingRatio',
  LifeNetworkRebufferingRatio: 'LifeNetworkRebufferingRatio',
  LifeAverageAvgBitrateKbps: 'LifeAverageAvgBitrateKbps',
  LifeAvgBitrateKbps: 'LifeAvgBitrateKbps',
  EndingBitrateKbps: 'EndingBitrateKbps',
  LifeAvgFramesPerSec: 'LifeAvgFramesPerSec',
  LifeVideoRestartTimeMs: 'LifeVideoRestartTimeMs',
  LifePlayingTimeMs: 'LifePlayingTimeMs',
  LifeCompletionPct: 'LifeCompletionPct',
  LifeEBVSWaitTimeMs: 'LifeEBVSWaitTimeMs',
  LifeJoinTimeMs: 'LifeJoinTimeMs',
  LifeNetworkRebufferingEvents: 'LifeNetworkRebufferingEvents',
  LifePodTimebeforePrerollExitSecs: 'LifePodTimebeforePrerollExitSecs',
  LifeVPFPlayingTimeMs: 'LifeVPFPlayingTimeMs',
  LifeVPFTPlayingTimeMs: 'LifeVPFTPlayingTimeMs',
  LifeVPFBPlayingTimeMs: 'LifeVPFBPlayingTimeMs',
  LifePausedTimePct: 'LifePausedTimePct',
  LifePausedTimeSecs: 'LifePausedTimeSecs',
  LifeAvgNumBitrateSwitches: 'LifeNumBitrateSwitches',
  JoinedAverageBitrate: 'JoinedAvgBitrateKbps',
  LifeVSFWaitTimeMs: 'LifeVSFWaitTimeMs',
  LifeVSFTWaitTimeMs: 'LifeVSFTWaitTimeMs',
  LifeVSFBWaitTimeMs: 'LifeVSFBWaitTimeMs',
  LifeExitRelatedCIRTimeMs: 'LifeExitRelatedCIRTimeMs',
  LifeDropFrameEndedPlays: 'LifeDropFrameEndedPlays',
};

export const BOOLEAN_METRICS = {
  EI: {
    [metricKeys.VideoStartFailures]: {
      dimension: 'IS_VIDEO_START_FAILURE',
      metric: metricKeys.VideoStartFailures,
    },
    [metricKeys.VideoStartFailuresTech]: {
      dimension: 'IS_VIDEO_START_FAILURE_TECH',
      metric: metricKeys.VideoStartFailuresTech,
    },
    [metricKeys.VideoStartFailuresBusiness]: {
      dimension: 'IS_VIDEO_START_FAILURE_BUSINESS',
      metric: metricKeys.VideoStartFailuresBusiness,
    },
    [metricKeys.VideoPlaybackFailures]: {
      dimension: 'IS_VIDEO_PLAYBACK_FAILURE',
      metric: metricKeys.VideoPlaybackFailures,
    },
    [metricKeys.VideoPlaybackFailuresTech]: {
      dimension: 'IS_VIDEO_PLAYBACK_FAILURE_TECH',
      metric: metricKeys.VideoPlaybackFailuresTech,
    },
    [metricKeys.VideoPlaybackFailuresTechPer1000PlayingMinutes]: {
      dimension: 'IS_VIDEO_PLAYBACK_FAILURE_TECH',
      metric: metricKeys.VideoPlaybackFailuresTech,
    },
    [metricKeys.VideoPlaybackFailuresBusiness]: {
      dimension: 'IS_VIDEO_PLAYBACK_FAILURE_BUSINESS',
      metric: metricKeys.VideoPlaybackFailuresBusiness,
    },
    [metricKeys.ExitBeforeVideoStarts]: {
      dimension: 'ExitBeforeVideoStart',
      metric: metricKeys.ExitBeforeVideoStarts,
    },
    [metricKeys.ExitDueToCIR]: {
      dimension: 'IS_EXIT_DUE_TO_CIR',
      metric: metricKeys.ExitDueToCIR,
    },
  },
  AI: {},
  APP: {},
  ODP: {},
}[CURRENT_PRODUCT_MODE];

// ECO_DISTRIBUTION_CONFIG has been moved to pulse-metrics repo
// Distribution configurations are now part of the preDefinedMetricsMeta
// and come from IFS via the getPreDefinedDistributionMetrics function

const getECODistributionDetails = () => {
  const preDefinedDistributionMetrics = getPreDefinedDistributionMetrics();
  const userDefinedDistributionMetric = getUserDefinedDistributionMetric();
  return { ...preDefinedDistributionMetrics, ...userDefinedDistributionMetric };
};

export const getDistributionMetrics = () =>
  ({
    EI: {
      [metricKeys.RebufferingRatio]: [
        {
          dimension: ENGAGEMENT.LifeRebufferingRatio,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'ratio',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.RebufferingRatio }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
        {
          dimension: ENGAGEMENT.LifeRebufferingRatio,
          metric: metricKeys.AverageLifePlayingTimeMins,
          yAxisUnit: 'min',
          xAxisUnit: 'ratio',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.RebufferingRatio }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AverageLifePlayingTimeMins }),
        },
      ],
      [metricKeys.ConnectionInducedRebufferingRatio]: [
        {
          dimension: ENGAGEMENT.LifeNetworkRebufferingRatio,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'ratio',
          xAxisLabel: getMetricDisplayName({
            metricKey: metricKeys.ConnectionInducedRebufferingRatio,
          }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
        {
          dimension: ENGAGEMENT.LifeNetworkRebufferingRatio,
          metric: metricKeys.AverageLifePlayingTimeMins,
          yAxisUnit: 'min',
          xAxisUnit: 'ratio',
          xAxisLabel: getMetricDisplayName({
            metricKey: metricKeys.ConnectionInducedRebufferingRatio,
          }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AverageLifePlayingTimeMins }),
        },
      ],
      [metricKeys.AvgBitrate]: [
        {
          dimension: ENGAGEMENT.LifeAverageAvgBitrateKbps,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'Kbps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AvgBitrate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
        {
          dimension: ENGAGEMENT.LifeAverageAvgBitrateKbps,
          metric: metricKeys.AverageLifePlayingTimeMins,
          yAxisUnit: 'min',
          xAxisUnit: 'Kbps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AvgBitrate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AverageLifePlayingTimeMins }),
        },
      ],
      [metricKeys.Bitrate]: [
        {
          dimension: ENGAGEMENT.LifeAvgBitrateKbps,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'Kbps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.Bitrate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
        {
          dimension: ENGAGEMENT.LifeAvgBitrateKbps,
          metric: metricKeys.AverageLifePlayingTimeMins,
          yAxisUnit: 'min',
          xAxisUnit: 'Kbps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.Bitrate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AverageLifePlayingTimeMins }),
        },
      ],
      [metricKeys.EndingBitrate]: [
        {
          dimension: ENGAGEMENT.EndingBitrateKbps,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'Kbps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndingBitrate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
        {
          dimension: ENGAGEMENT.EndingBitrateKbps,
          metric: metricKeys.AverageLifePlayingTimeMins,
          yAxisUnit: 'min',
          xAxisUnit: 'Kbps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndingBitrate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AverageLifePlayingTimeMins }),
        },
      ],
      [metricKeys.Framerate]: [
        {
          dimension: ENGAGEMENT.LifeAvgFramesPerSec,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'fps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.Framerate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.VideoRestartTime]: [
        {
          dimension: ENGAGEMENT.LifeVideoRestartTimeMs,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoRestartTime }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.Minutes]: [
        {
          dimension: ENGAGEMENT.LifePlayingTimeMs,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.PLAYING_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.PercentageComplete]: [
        {
          dimension: ENGAGEMENT.LifeCompletionPct,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: '%',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.PercentageComplete }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.ExitBeforeVideoStarts]: [
        {
          dimension: ENGAGEMENT.LifeEBVSWaitTimeMs,
          metric: metricKeys.ExitBeforeVideoStarts,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.WAIT_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.ExitBeforeVideoStarts }),
        },
      ],
      [metricKeys.VideoStartTime]: [
        {
          dimension: ENGAGEMENT.LifeJoinTimeMs,
          metric: metricKeys.PlaysWithValidJoinTime,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoStartTime }),
          yAxisLabel: AXIS_NAME.PLAYS,
        },
      ],
      [metricKeys.NonZeroCIRREndedPlays]: [
        {
          dimension: ENGAGEMENT.LifeNetworkRebufferingEvents,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'ratio',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.NonZeroCIRREndedPlays }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
        {
          dimension: ENGAGEMENT.LifeNetworkRebufferingEvents,
          metric: metricKeys.AverageLifePlayingTimeMins,
          yAxisUnit: 'min',
          xAxisUnit: 'ratio',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.NonZeroCIRREndedPlays }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.AverageLifePlayingTimeMins }),
        },
      ],
      [metricKeys.ExitsDuringPreRoll]: [
        {
          dimension: ENGAGEMENT.LifePodTimebeforePrerollExitSecs,
          metric: metricKeys.ExitsDuringPreRoll,
          yAxisUnit: '',
          xAxisUnit: 'sec',
          xAxisLabel: AXIS_NAME.AD_PLAYING_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.ExitsDuringPreRoll }),
        },
      ],
      [metricKeys.VideoPlaybackFailures]: [
        {
          dimension: ENGAGEMENT.LifeVPFPlayingTimeMs,
          metric: metricKeys.VideoPlaybackFailures,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.PLAYING_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoPlaybackFailures }),
        },
      ],
      [metricKeys.VideoPlaybackFailuresTech]: [
        {
          dimension: ENGAGEMENT.LifeVPFTPlayingTimeMs,
          metric: metricKeys.VideoPlaybackFailuresTech,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.PLAYING_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoPlaybackFailuresTech }),
        },
      ],
      // The rate uses the same failure-count histogram as VPF-T.
      [metricKeys.VideoPlaybackFailuresTechPer1000PlayingMinutes]: [
        {
          dimension: ENGAGEMENT.LifeVPFTPlayingTimeMs,
          metric: metricKeys.VideoPlaybackFailuresTech,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.PLAYING_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoPlaybackFailuresTech }),
        },
      ],
      [metricKeys.VideoPlaybackFailuresBusiness]: [
        {
          dimension: ENGAGEMENT.LifeVPFBPlayingTimeMs,
          metric: metricKeys.VideoPlaybackFailuresBusiness,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.PLAYING_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoPlaybackFailuresBusiness }),
        },
      ],
      [metricKeys.PausedRatio]: [
        {
          dimension: ENGAGEMENT.LifePausedTimePct,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: '%',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.PausedRatio }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.PausedTime]: [
        {
          dimension: ENGAGEMENT.LifePausedTimeSecs,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'sec',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.PausedTime }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.LifeAvgNumBitrateSwitches]: [
        {
          dimension: ENGAGEMENT.LifeAvgNumBitrateSwitches,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'unitless',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.LifeAvgNumBitrateSwitches }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.LifeJoinedAvgBitrate]: [
        {
          dimension: ENGAGEMENT.JoinedAverageBitrate,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'Kbps',
          xAxisLabel: getMetricDisplayName({ metricKey: metricKeys.LifeJoinedAvgBitrate }),
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
      [metricKeys.VideoStartFailures]: [
        {
          dimension: ENGAGEMENT.LifeVSFWaitTimeMs,
          metric: metricKeys.VideoStartFailures,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.WAIT_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoStartFailures }),
        },
      ],
      [metricKeys.VideoStartFailuresTech]: [
        {
          dimension: ENGAGEMENT.LifeVSFTWaitTimeMs,
          metric: metricKeys.VideoStartFailuresTech,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.WAIT_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoStartFailuresTech }),
        },
      ],
      [metricKeys.VideoStartFailuresBusiness]: [
        {
          dimension: ENGAGEMENT.LifeVSFBWaitTimeMs,
          metric: metricKeys.VideoStartFailuresBusiness,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.WAIT_TIME,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.VideoStartFailuresBusiness }),
        },
      ],
      [metricKeys.ExitDueToCIR]: [
        {
          dimension: ENGAGEMENT.LifeExitRelatedCIRTimeMs,
          metric: metricKeys.ExitDueToCIR,
          yAxisUnit: '',
          xAxisUnit: 'ms',
          xAxisLabel: AXIS_NAME.CIR_TIME_PRECEDING_EXIT,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.ExitDueToCIR }),
        },
      ],
      [metricKeys.LifeDropFrameRatio]: [
        {
          dimension: ENGAGEMENT.LifeDropFrameEndedPlays,
          metric: metricKeys.EndedPlays,
          yAxisUnit: '',
          xAxisUnit: 'ratio',
          xAxisLabel: AXIS_NAME.LifeDropFrameRatio,
          yAxisLabel: getMetricDisplayName({ metricKey: metricKeys.EndedPlays }),
        },
      ],
    },
    AI: {},
    APP: getECODistributionDetails(),
    ODP: {},
  })[CURRENT_PRODUCT_MODE];

export const getDistributionByMetric = (metricKey: string) => {
  return getDistributionMetrics()?.[metricKey];
};

export const getDistributionDimensionByMetric = (metricKey: string) => {
  return getDistributionMetrics()?.[metricKey]?.map((item: any) => item.dimension);
};

export const getDistributionByMetricForViewerSessions = (metricKey: string) => {
  const distributionConfig = getDistributionMetrics();
  // other than VSF and VPF metrics, all other metrics needs metric filter to show viewer sessions
  if (isEiModule) {
    const vsfVpfMetrics = [
      metricKeys.VideoStartFailures,
      metricKeys.VideoStartFailuresTech,
      metricKeys.VideoStartFailuresBusiness,
      metricKeys.VideoPlaybackFailures,
      metricKeys.VideoPlaybackFailuresTech,
      metricKeys.VideoPlaybackFailuresTechPer1000PlayingMinutes,
      metricKeys.VideoPlaybackFailuresBusiness,
    ];
    if (vsfVpfMetrics.includes(metricKey)) {
      return null;
    }
  }
  return distributionConfig?.[metricKey];
};

/**
 * Reverse lookup: Find the metric key for a given distribution dimension
 * Searches through both distribution metrics and boolean metrics
 */
export const getMetricByDistributionDimension = (dimension: string): string | null => {
  // Check boolean metrics first (direct mapping)
  if (BOOLEAN_METRICS) {
    const booleanEntry = Object.entries(BOOLEAN_METRICS).find(([_, config]) => {
      const configObj = config as { dimension: string; metric: string };
      return configObj.dimension === dimension;
    });
    if (booleanEntry) {
      const configObj = booleanEntry[1] as { dimension: string; metric: string };
      return configObj.metric;
    }
  }

  // Search distribution metrics
  const distributionMetrics = getDistributionMetrics();
  if (distributionMetrics) {
    for (const [metricKey, configs] of Object.entries(distributionMetrics)) {
      if (Array.isArray(configs)) {
        const found = configs.some((config: any) => config.dimension === dimension);
        if (found) {
          return metricKey;
        }
      }
    }
  }

  return null;
};
